import React, { useEffect } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { SignedIn, SignedOut, SignOutButton, useAuth } from '@clerk/clerk-react';
import { ToastProvider } from './contexts/ToastContext.jsx';
import { ThemeProvider } from './contexts/ThemeContext.jsx';
import { NotificationProvider, useNotifications } from './contexts/NotificationContext.jsx';
import { RealTimeStatusProvider } from './contexts/RealTimeStatusContext.jsx';
import RealTimeAnimationManager from './components/RealTimeAnimationManager.jsx';
import { setGlobalAuthErrorHandler } from './utils/apiClient.js';
import ErrorBoundary from './components/ErrorBoundary.jsx';
import DashboardLayout from './components/DashboardLayout.jsx';
import ThemeConsistencyChecker from './components/ThemeConsistencyChecker.jsx';
import IndexingStatusIndicator from './components/IndexingStatusIndicator.jsx';

import AdminNavLink from './components/AdminNavLink.jsx';

// Critical components loaded immediately
import Landing from './pages/Landing.jsx';
import Dashboard from './pages/Dashboard.jsx';
import SignInPage from './pages/SignIn.jsx';
import SignUpPage from './pages/SignUp.jsx';

// Import critical components immediately to avoid React 19 lazy loading issues
import Plans from './pages/Plans.jsx';
import ProfilePage from './pages/Profile.jsx';
import EnhancedOnboarding from './components/EnhancedOnboarding.jsx';
import PhoneNumbers from './pages/PhoneNumbers.jsx';
import CallForwarding from './pages/CallForwarding.jsx';
import ParrotConfiguration from './pages/ParrotConfiguration.jsx';
import ParrotConfigurationChat from './pages/ParrotConfigurationChat.jsx';
import ParrotsList from './pages/ParrotsList.jsx';
import ParrotNew from './pages/ParrotNew.jsx';
import ParrotEdit from './pages/ParrotEdit.jsx';
import ParrotWizardStep1 from './pages/ParrotWizardStep1.jsx';
import ParrotWizardStep2 from './pages/ParrotWizardStep2.jsx';
import ParrotWizardStep3 from './pages/ParrotWizardStep3.jsx';
import PricingPage from './pages/Pricing.jsx';
import BillingPortal from './pages/BillingPortal.jsx';
import RecordingsPage from './pages/Recordings.jsx';
import UserDetails from './pages/UserDetails.jsx';
import ProfileDetails from './pages/ProfileDetails.jsx';
import KnowledgeBase from './pages/KnowledgeBase.jsx';
import Conversations from './pages/Conversations.jsx';
import UserNotifications from './pages/UserNotifications.jsx';

// Test components (development only)
import KnowledgeBaseToggleTest from './test-pages/KnowledgeBaseToggleTest.jsx';

// Import all admin components directly
import AdminDashboard from './pages/AdminDashboard.jsx';
import AdminRoute from './components/AdminRoute.jsx';
import AdminLayout from './components/AdminLayout.jsx';
import AdminErrorBoundary from './components/AdminErrorBoundary.jsx';
import AdminOverview from './pages/admin/AdminOverview.jsx';
import UserManagement from './pages/admin/UserManagement.jsx';
import ParrotManagement from './pages/admin/ParrotManagement.jsx';
import KnowledgeBaseManagement from './components/admin/KnowledgeBaseManagement.jsx';
import AuditLogViewer from './components/admin/AuditLogViewer.jsx';
import PhoneNumberManagement from './pages/admin/PhoneNumberManagement.jsx';
import PhoneDeletionQueue from './pages/admin/PhoneDeletionQueue.jsx';
import QuickActionTemplateManagement from './pages/admin/QuickActionTemplateManagement.jsx';
import AdminSettings from './pages/admin/AdminSettings.jsx';
import AdminAnalytics from './pages/admin/AdminAnalytics.jsx';
import AdminSystemHealth from './pages/admin/AdminSystemHealth.jsx';
import AdminNotifications from './pages/admin/AdminNotifications.jsx';
import AdminCallRecordings from './pages/admin/AdminCallRecordings.jsx';
import AdminSignIn from './pages/AdminSignIn.jsx';

// Component to handle route-based notification refresh
const RouteNotificationRefresh = () => {
  const location = useLocation();
  const { refreshUnreadCount } = useNotifications();

  useEffect(() => {
    // Only refresh notifications on dashboard routes where user should be authenticated
    const dashboardRoutes = [
      '/dashboard',
      '/notifications',
      '/conversations',
      '/parrots',
      '/phone-numbers',
      '/knowledge-base',
      '/recordings',
      '/call-forwarding',
      '/billing',
      '/profile'
    ];

    const isDashboardRoute = dashboardRoutes.some(route =>
      location.pathname === route || location.pathname.startsWith(route + '/')
    );

    if (isDashboardRoute) {
      // Refresh notifications when route changes on authenticated pages
      refreshUnreadCount();
    }
  }, [location.pathname, refreshUnreadCount]);

  return null;
};

// Component to handle global authentication errors and redirect to login
const AuthErrorHandler = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isSignedIn, isLoaded } = useAuth();

  useEffect(() => {
    // Define protected routes that require authentication
    const protectedRoutes = [
      '/dashboard',
      '/notifications',
      '/conversations',
      '/parrots',
      '/phone-numbers',
      '/knowledge-base',
      '/recordings',
      '/call-forwarding',
      '/billing',
      '/profile',
      '/admin'
    ];

    const isProtectedRoute = protectedRoutes.some(route =>
      location.pathname === route || location.pathname.startsWith(route + '/')
    );

    // If user is on a protected route but not signed in (after Clerk has loaded)
    if (isLoaded && !isSignedIn && isProtectedRoute) {
      console.log('User not authenticated on protected route, redirecting to sign-in');
      navigate('/sign-in', { replace: true });
    }
  }, [isSignedIn, isLoaded, location.pathname, navigate]);

  // Listen for global unhandled promise rejections (API errors)
  useEffect(() => {
    const handleUnhandledRejection = (event) => {
      const reason = event.reason;

      // Check if it's an authentication error from our API client
      if (reason && (reason.status === 401 || reason.message?.includes('Authentication required'))) {
        console.log('Authentication error detected, redirecting to sign-in');
        navigate('/sign-in', { replace: true });
      }
    };

    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [navigate]);

  return null;
};

// Component to wrap dashboard pages with DashboardLayout
const DashboardRoute = ({ children }) => (
  <DashboardLayout>
    {children}
  </DashboardLayout>
);

// Component for non-dashboard pages that need the top navigation
const TopNavRoute = ({ children }) => (
  <div className="min-h-screen bg-gray-900 text-white dark:text-white">
    {/* Nav bar for non-dashboard authenticated sections */}
    <header className="bg-gray-800 shadow-md p-4 flex justify-between items-center fixed top-0 left-0 w-full z-10">
      <Link to="/dashboard" className="flex items-center gap-2">
        <img src="/logo.svg" alt="ParrotMate Logo" className="h-8 w-8" />
        <span className="font-bold text-xl text-teal-400">ParrotMate</span>
      </Link>
      <div className="flex items-center gap-4">
        <SignedIn>
          <Link to="/dashboard" className="text-teal-400 hover:text-teal-300 font-medium">Dashboard</Link>
          <AdminNavLink />
          <SignOutButton signOutOptions={{ redirectUrl: '/' }}>
            <button className="text-teal-400 hover:text-teal-300 font-medium">Sign Out</button>
          </SignOutButton>
        </SignedIn>
        <SignedOut>
          <Link to="/sign-in" className="text-teal-400 hover:text-teal-300 font-medium">Sign In</Link>
        </SignedOut>
      </div>
    </header>

    <main className="pt-24 p-6">
      {children}
    </main>
  </div>
);

export default function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut } = useAuth();

  // Set up global auth error handler
  useEffect(() => {
    const handleAuthError = (authError) => {
      console.log('Global auth error detected, signing out and redirecting to sign-in');

      // Sign out the user from Clerk to clear any stale session
      signOut().then(() => {
        navigate('/sign-in', { replace: true });
      }).catch(() => {
        // If signOut fails, still redirect to sign-in
        navigate('/sign-in', { replace: true });
      });
    };

    setGlobalAuthErrorHandler(handleAuthError);
  }, [navigate, signOut]);

  if (location.pathname === '/') {
    return <Landing />;
  }

  return (
    <ErrorBoundary fallbackMessage="The application encountered an unexpected error. Please refresh the page to continue.">
      <ThemeProvider>
        <ThemeConsistencyChecker>
          <ToastProvider>
          <ErrorBoundary fallbackMessage="There was an error with notifications. The app will continue to work.">
            <NotificationProvider>
              <ErrorBoundary fallbackMessage="There was an error with real-time features. The app will continue to work.">
                <RealTimeStatusProvider>
                  <RouteNotificationRefresh />
                  <AuthErrorHandler />
                  <RealTimeAnimationManager />
                  <ErrorBoundary fallbackMessage="There was an error loading this page. Please try refreshing or navigate to a different section.">
                    <Routes>
                      {/* Routes that use DashboardLayout - all protected by authentication */}
                      <Route path="/dashboard" element={
                        <DashboardRoute>
                          <Dashboard />
                        </DashboardRoute>
                      } />
                      <Route path="/notifications" element={
                        <DashboardRoute>
                          <UserNotifications />
                        </DashboardRoute>
                      } />
                      <Route path="/conversations" element={
                        <DashboardRoute>
                          <Conversations />
                        </DashboardRoute>
                      } />
                      <Route path="/parrots" element={
                        <DashboardRoute>
                          <ParrotsList />
                        </DashboardRoute>
                      } />
                      <Route path="/parrots/new" element={
                        <DashboardRoute>
                          <ParrotNew />
                        </DashboardRoute>
                      } />
                      {/* New Wizard Routes */}
                      <Route path="/parrots/new/step1" element={
                        <DashboardRoute>
                          <ParrotWizardStep1 />
                        </DashboardRoute>
                      } />
                      <Route path="/parrots/new/step2" element={
                        <DashboardRoute>
                          <ParrotWizardStep2 />
                        </DashboardRoute>
                      } />
                      <Route path="/parrots/new/step3" element={
                        <DashboardRoute>
                          <ParrotWizardStep3 />
                        </DashboardRoute>
                      } />
                      {/* Edit Wizard Routes */}
                      <Route path="/parrots/:id/edit" element={
                        <DashboardRoute>
                          <ParrotEdit />
                        </DashboardRoute>
                      } />
                      <Route path="/parrots/:id/edit/step1" element={
                        <DashboardRoute>
                          <ParrotWizardStep1 />
                        </DashboardRoute>
                      } />
                      <Route path="/parrots/:id/edit/step2" element={
                        <DashboardRoute>
                          <ParrotWizardStep2 />
                        </DashboardRoute>
                      } />
                      <Route path="/parrots/:id/edit/step3" element={
                        <DashboardRoute>
                          <ParrotWizardStep3 />
                        </DashboardRoute>
                      } />
                      <Route path="/phone-numbers" element={
                        <DashboardRoute>
                          <PhoneNumbers />
                        </DashboardRoute>
                      } />
                      <Route path="/knowledge-base" element={
                        <DashboardRoute>
                          <KnowledgeBase />
                        </DashboardRoute>
                      } />
                      <Route path="/recordings" element={
                        <DashboardRoute>
                          <RecordingsPage />
                        </DashboardRoute>
                      } />
                      <Route path="/call-forwarding" element={
                        <DashboardRoute>
                          <CallForwarding />
                        </DashboardRoute>
                      } />
                      <Route path="/billing" element={
                        <DashboardRoute>
                          <BillingPortal />
                        </DashboardRoute>
                      } />
                      <Route path="/profile" element={
                        <DashboardRoute>
                          <ProfilePage />
                        </DashboardRoute>
                      } />
                      <Route path="/profile/details" element={
                        <DashboardRoute>
                          <ProfileDetails />
                        </DashboardRoute>
                      } />

                      {/* Routes that use top navigation */}
                      <Route path="/plans" element={
                        <TopNavRoute>
                          <Plans />
                        </TopNavRoute>
                      } />
                      <Route path="/sign-in/*" element={<SignInPage />} />
                      
                      {/* Test Routes - Only in development */}
                      {import.meta.env.DEV && (
                        <Route path="/test/kb-toggle" element={
                          <DashboardRoute>
                            <KnowledgeBaseToggleTest />
                          </DashboardRoute>
                        } />
                      )}
                      <Route path="/sign-up/*" element={<SignUpPage />} />
                      <Route path="/admin-sign-in" element={<AdminSignIn />} />
                      <Route path="/user-details" element={
                        <TopNavRoute>
                          <UserDetails />
                        </TopNavRoute>
                      } />
                      <Route path="/onboarding" element={<EnhancedOnboarding />} />
                      <Route path="/parrot-configuration" element={
                        <TopNavRoute>
                          <ParrotConfigurationChat />
                        </TopNavRoute>
                      } />
                      <Route path="/parrot-configuration/form" element={
                        <TopNavRoute>
                          <ParrotConfiguration />
                        </TopNavRoute>
                      } />
                      <Route path="/pricing" element={
                        <TopNavRoute>
                          <PricingPage />
                        </TopNavRoute>
                      } />

                      {/* Legacy admin route - redirect to new structure */}
                      <Route path="/admin-legacy" element={<AdminDashboard />} />

                      {/* New admin routes with proper layout and protection */}
                      <Route path="/admin/*" element={
                        <AdminRoute>
                          <AdminErrorBoundary fallbackMessage="An error occurred in the admin interface. Please try refreshing the page.">
                            <AdminLayout>
                              <Routes>
                                <Route index element={<AdminOverview />} />
                                <Route path="users" element={<UserManagement />} />
                                <Route path="parrots" element={<ParrotManagement />} />
                                <Route path="knowledge-base" element={<KnowledgeBaseManagement />} />
                                <Route path="phone-numbers" element={<PhoneNumberManagement />} />
                                <Route path="phone-queue" element={<PhoneDeletionQueue />} />
                                <Route path="audit" element={<AuditLogViewer />} />
                                <Route path="notifications" element={<AdminNotifications />} />
                                <Route path="quick-templates" element={<QuickActionTemplateManagement />} />
                                <Route path="analytics" element={<AdminAnalytics />} />
                                <Route path="system-health" element={<AdminSystemHealth />} />
                                <Route path="call-recordings" element={<AdminCallRecordings />} />
                                <Route path="settings" element={<AdminSettings />} />
                              </Routes>
                            </AdminLayout>
                          </AdminErrorBoundary>
                        </AdminRoute>
                      } />
                    </Routes>
                  </ErrorBoundary>
                </RealTimeStatusProvider>
              </ErrorBoundary>
            </NotificationProvider>
          </ErrorBoundary>
        </ToastProvider>
        </ThemeConsistencyChecker>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
